import { Button, FieldText, FlexBox, Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import * as React from 'react';
import isURL from 'validator/lib/isURL';

import styles from './linkMenu.module.scss';

type LinkMenuProps = {
  /** Extra classnames */
  className?: string;
  /** Text initial value */
  initialText: string;
  /** Url initial value */
  initialUrl: string;
  /** On add link callback */
  onAddLink: (url: string, text: string) => void;
  /** Callback for hiding the link menu */
  onHide: () => void;
  /** Callback for unlinking */
  onUnlink: () => void;
};

/**
 * Renders the menu that appears when the link button is pressed. Allows a link
 * to be added/edited or removed.
 */
export function LinkMenu({
  className,
  initialText,
  initialUrl,
  onHide,
  onAddLink,
  onUnlink,
  ...otherProps
}: LinkMenuProps): JSX.Element {
  const [textValue, setTextValue] = useState(initialText);
  const [urlValue, setUrlValue] = useState(initialUrl);
  const [textTouched, setTextTouched] = useState(false);
  const [urlTouched, setUrlTouched] = useState(false);

  useEffect(() => {
    setTextValue(initialText);
    setUrlValue(initialUrl);
  }, [initialText, initialUrl]);

  const reset = () => {
    setTextValue('');
    setUrlValue('');
    setTextTouched(false);
    setUrlTouched(false);
  };

  const handleAddLink = (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e?.preventDefault();
    onAddLink(urlValue, textValue);
    onHide();
    reset();
  };

  const handleUnlink = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    onUnlink();
    onHide();
    reset();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isUrlValid && handleAddLink) {
      e.preventDefault();
      handleAddLink();
    }
  };

  const rootClass = classnames({ [styles.root]: true }, className);
  const isUrlValid = isURL(urlValue, { require_protocol: true });
  const hasText = textValue.trim().length > 0;

  return (
    <div {...otherProps} className={rootClass}>
      <Text.Heading withMarginBottom>Edit Link</Text.Heading>

      <FieldText
        autoFocus={!hasText}
        label="Link title"
        className={styles.field}
        onKeyDown={handleKeyDown}
        meta={{
          error: textTouched && !hasText ? 'Please enter a title' : undefined,
        }}
        input={{
          value: textValue,
          name: 'pleasedontautofillthis',
          onBlur: () => setTextTouched(true),
          onChange: (e) => setTextValue(e?.target.value || ''),
        }}
      />

      <FieldText
        autoFocus={hasText}
        label="Link URL"
        className={styles.field}
        onKeyDown={handleKeyDown}
        meta={{
          error:
            urlTouched && !isUrlValid ? 'Please enter a valid url' : undefined,
        }}
        input={{
          value: urlValue,
          name: 'link_url',
          onBlur: () => setUrlTouched(true),
          onChange: (e) => setUrlValue(e?.target.value || ''),
        }}
      />

      <FlexBox justifyContent="space-between">
        {initialUrl ? (
          <Button variant="tertiary" onClick={handleUnlink}>
            Remove link
          </Button>
        ) : (
          <span />
        )}

        <Button disabled={!isUrlValid || !hasText} onClick={handleAddLink}>
          Save
        </Button>
      </FlexBox>
    </div>
  );
}
